import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { dt } from "@/lib/format";
import { Bell } from "lucide-react";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { playNotificationSound } from "@/lib/sound";
import { useBrand } from "@/components/BrandProvider";

const TIPO_ROUTE = {
  derivacion: "/conversaciones",
  pago_pendiente: "/pagos",
  pago_validado: "/pagos",
  stock_bajo: "/productos",
  seguimiento_fallido: "/seguimientos",
  pedido_requiere_revision: "/pedidos",
  pedido_nuevo: "/pedidos",
  aviso_pago_no_enviado: "/conversaciones", // Fix O (2026-09-24): aviso de pago frenado por "Solo recibir"
  whatsapp_desconectado: "/canales", // Fix P (2026-10-01): el número real se desconectó de YCloud
};

export function NotificationBell() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [prefs, setPrefs] = useState(null);
  const nav = useNavigate();
  const marca = useBrand() || {};
  const knownIds = useRef(new Set());
  const firstLoad = useRef(true);

  const load = () => api.get("/notifications").then((r) => {
    const nuevos = r.data.filter((n) => !n.leida && !knownIds.current.has(n.id));
    if (!firstLoad.current && nuevos.length > 0) {
      if (prefs?.sonido !== false) playNotificationSound();
      nuevos.forEach((n) => {
        if (typeof Notification !== "undefined" && Notification.permission === "granted") {
          try {
            const nn = new Notification(`${marca.nombre_comercial || "Marthitashop"} · ${n.titulo}`, { body: n.mensaje });
            nn.onclick = () => { window.focus(); nav(TIPO_ROUTE[n.tipo] || "/"); nn.close(); };
          } catch (e) { /* ignore */ }
        }
      });
    }
    r.data.forEach((n) => knownIds.current.add(n.id));
    firstLoad.current = false;
    setItems(r.data);
  }).catch(() => {});

  useEffect(() => {
    api.get("/notifications/preferencias").then((r) => setPrefs(r.data)).catch(() => {});
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const unread = items.filter((i) => !i.leida).length;

  useEffect(() => {
    const base = marca.nombre_comercial || "Marthitashop";
    document.title = unread > 0 ? `(${unread > 9 ? "9+" : unread}) ${base}` : base;
  }, [unread, marca.nombre_comercial]);

  const markAll = async () => { await api.post("/notifications/marcar-leidas"); load(); };
  const goTo = (n) => { setOpen(false); nav(TIPO_ROUTE[n.tipo] || "/"); };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          data-testid="notification-bell"
          className="relative text-foreground/70 hover:text-primary transition-colors p-1.5"
        >
          <Bell className="h-5 w-5" strokeWidth={1.5} />
          {unread > 0 && (
            <span data-testid="notification-badge" className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-[10px] rounded-full h-4 w-4 flex items-center justify-center">
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        data-testid="notification-dropdown"
        align="end"
        side="bottom"
        sideOffset={8}
        collisionPadding={12}
        avoidCollisions
        className="z-[100] w-[380px] max-w-[92vw] max-h-[70vh] overflow-y-auto p-2 rounded-2xl"
      >
        <div className="flex items-center justify-between px-2 py-1">
          <span className="text-sm font-semibold">Notificaciones</span>
          {unread > 0 && (
            <button onClick={markAll} className="text-xs text-primary" data-testid="mark-all-read">
              Marcar todas como leídas
            </button>
          )}
        </div>
        {items.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-6">Sin notificaciones.</p>
        ) : items.map((n) => (
          <button
            key={n.id}
            onClick={() => goTo(n)}
            data-testid={`notification-${n.id}`}
            className={`w-full text-left rounded-xl px-3 py-2 mb-1 transition-colors ${n.leida ? "hover:bg-accent" : "bg-accent/60 hover:bg-accent"}`}
          >
            <p className="text-sm font-medium">{n.titulo}</p>
            <p className="text-xs text-muted-foreground">{n.mensaje}</p>
            <p className="text-[10px] text-muted-foreground/70 mt-0.5">{dt(n.created_at)}</p>
          </button>
        ))}
      </PopoverContent>
    </Popover>
  );
}
